/**
 * context.api.ts
 *
 * This file is responsible for enabling prioritizing data API calls to fetch critical application context
 * for the reactJS application.  This context is MVP data that is required for rendeirng first paint of
 * a page as well as enabling authorization at the top of the user waterfall.
 *
 * Currently, this context essentially contains three parts:
 *
 * - header
 * - udData
 * - footer
 *
 * Additionally, this data needs to be handled different depending on whether you are in a server or
 * client context.  If we are resolving this data on the server, we want to be fetching non user specific,
 * generic data to maximize our ability cache the page.  We will render a `<script />` tag to call the
 * handler in this file in order to initiate a request for context on the client as early as possible.
 *
 * Authorization can be performed either by:
 *
 * - Rendering a redirect javascript function inline with the document (fastest possible redirect)
 * - Implemented in `_app.page.tsx` in a `useEffect()` hook to leverage a nicer auth UX experience
 *
 * This is a standard API file in Udemy Frontends projects.  Data resolution can vary between applications,
 * and common functions are defined in `@udemy/nextjs-core`
 */

import {AxiosError} from 'axios';
import memoize from 'memoizee';
import {NextApiRequest, NextApiResponse} from 'next/types';

import {
    CommonAppContextData,
    getHeaderAndFooterData,
    getHeaderNavData,
    getStaticUDData,
    getUserSpecificUDData,
    isUFBRequest,
    JavascriptResponse,
    ONE_DAY,
} from '@udemy/nextjs-core';
import {DesktopInstructorHeaderProps} from '@udemy/react-header';
import {AuthenticatedUser, UDData} from '@udemy/ud-data';

import {i18nPathToBundleMap} from 'next.config.i18n';

import {getRuntimeConfig} from './config.api';
import {getInstructorHeaderData} from './instructor-header-data';
import {getTipaltiPaymentHistoryLink} from './tipalti-payment-history-link';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AppContextData extends CommonAppContextData {
    instructorHeaderData?: Pick<DesktopInstructorHeaderProps, 'instructorContext'>;
    tipaltiPaymentHistoryLink?: string;
}

/**
 * getStaticAppContext()
 *
 * This function is responsible for fetching static application context required for pre-rendering
 * cachable pages.  The information returned in this request should not contain any user specific information or
 * specific information that is unique to the incoming request.
 *
 * Each application may want to customize the data they are requesting.
 * Over time, data should be resolved from `api-platform-graphql` instead.
 *
 * @param options.locale - Locale to request
 */
export async function _getStaticAppContext({locale}: {locale: string}): Promise<AppContextData> {
    const monolithBaseUrl = getRuntimeConfig('MONOLITH_URL');
    const [{header, footer}, udData] = await Promise.all([
        getHeaderAndFooterData({locale, monolithBaseUrl}),
        getStaticUDData({locale, monolithBaseUrl}),
    ]);
    const appContext = {header, udData, footer};

    // Make sure to throw an exception if we are unable to resolve any appContext data
    if (!header || !udData || !footer) {
        throw new Error('failed to resolve appContext');
    }
    return appContext;
}

// Memoize `_getStaticAppContext` for performance
export const getStaticAppContext = memoize(_getStaticAppContext, {
    promise: true,
    maxAge: ONE_DAY,
    normalizer: function (args) {
        return JSON.stringify(args[0]);
    },
});

/**
 * getUserSpecificAppContext()
 *
 * Similar to `getStaticAppContext()`, this function is responsible for fetching UD data to hydrate and authorize
 * the application on the client side.  The data that is requested here should vary from what is requested in
 * `getStaticAppContext()`.  This should resolve user specific information including authorizing whether or not a user
 * should be able to access this page.
 *
 * @param options.locale {string} - Locale of incoming request
 * @param req
 * @returns
 */
export async function getUserSpecificAppContext(
    {locale}: {locale: string},
    req: NextApiRequest,
): Promise<AppContextData> {
    const monolithBaseUrl = getRuntimeConfig('MONOLITH_URL');
    const isUFB = isUFBRequest(req);
    const [{header, footer}, udData, headerNavData, instructorHeaderData] = await Promise.all([
        getHeaderAndFooterData({locale, monolithBaseUrl}, req),
        getUserSpecificUDData({locale, monolithBaseUrl}, req),
        getHeaderNavData({locale, isUFB, monolithBaseUrl}, req),
        getInstructorHeaderData({locale, monolithBaseUrl}, req),
    ]);
    const authenticatedUser = udData.me as AuthenticatedUser;
    const tipaltiPaymentHistoryLink = await getTipaltiPaymentHistoryLink({
        userId: authenticatedUser.id,
        locale: authenticatedUser.locale,
    });
    // eslint-disable-next-line no-console
    // console.log('locale:' + locale);
    // eslint-disable-next-line no-console
    // console.log('ud.locale:' + authenticatedUser.locale);
    header.navigationCategories = headerNavData;
    return {header, udData, footer, instructorHeaderData, tipaltiPaymentHistoryLink};
}

/**
 * handler()
 *
 * This is the handler that nextJS executes when we receive a request from the `<script />` tag
 * that we render in `<head />` to prioritize request for client context.
 *
 * This handler should return `application/javascript` that contains the required client application
 * context to hydrate / authorization the application.
 *
 * Optionally, a `window.location.assign()` can be written here to redirect the user somewhere else
 * if they are not sufficiently authorized. (Can read `header` and `udData` to determine authorization state).
 *
 * Authorization can also be done in the `useEffect()` hook of `_app.page.tsx`
 *
 * @param req - client request
 * @param res - application/javascript response
 */
export default async function handler(req: NextApiRequest, res: NextApiResponse) {
    const javascriptResponse = new JavascriptResponse();

    // Get locale from the query params
    const nextjsLocale = new URL(req.url as string, 'https://www.udemy.com/').searchParams.get(
        'locale',
    );
    const locale = i18nPathToBundleMap[nextjsLocale as keyof typeof i18nPathToBundleMap];

    // Create empty app context
    let appContextData: AppContextData = {} as AppContextData;

    try {
        const {header, udData, footer, instructorHeaderData, tipaltiPaymentHistoryLink} =
            await getUserSpecificAppContext({locale}, req);
        appContextData = {
            header,
            udData: udData as UDData,
            footer,
            instructorHeaderData,
            tipaltiPaymentHistoryLink,
        };
    } catch (e) {
        if ((e as AxiosError).isAxiosError) {
            const axiosError = e as AxiosError;
            const axiosErrorDetails = {
                ...axiosError.toJSON(),
                responseHeaders: axiosError?.response?.headers,
            };
            // eslint-disable-next-line no-console
            console.error(
                'Failed to fetch client app context due to axios error',
                axiosErrorDetails,
            );
        } else {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch client app context', e);
        }
        // We failed to fetch full app request context.  This will cause issues in the client, so send a message
        javascriptResponse.logError(`Failed to fetch client app context: ${(e as Error).message}`);
        javascriptResponse.sendResponse(res, 207);
        return;
    }

    javascriptResponse.assignAppContext(appContextData);

    // Authorization checks here:
    // If we're on a UB site but not logged in, redirect to home
    const udData = appContextData.udData;
    const referer = new URL(req.url as string, 'https://www.udemy.com/');
    const refererHost = req?.headers['x-udemy-origin-host'] || referer.host;
    const forwardedHost = refererHost;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const returnHost = forwardedHost;
    const isLoggedOutUB = udData.Config.brand.has_organization && !udData.me.is_authenticated;
    if (isLoggedOutUB) {
        javascriptResponse.logMessage(
            'User not authenticated or does not belong to this organization. Redirecting to home page.',
        );
        javascriptResponse.redirectToHome();
        javascriptResponse.sendResponse(res, 207);
        return;
    }

    javascriptResponse.sendResponse(res);
}
